import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useAppSelector } from 'src/services/redux/app/store';

const REACT_APP_API_URL = `${process.env.REACT_APP_API_URL}`;

export const useCustomersList = (params: any, onError?: any) => {
  const { user } = useAppSelector((state) => state.auth);
  const requestParams = { company_id: user?.company_id, ...params, sales_staff: user?.id };
  const requestUrl = REACT_APP_API_URL + '/customers/list';
  const fetchData = async () => {
    const res = await axios.get<any>(`${requestUrl}`, { params: requestParams });
    return res?.data || {};
  };
  return useQuery(['customers-list', params], fetchData, {
    refetchOnWindowFocus: false,
    onError,
    staleTime: Infinity,
    retry: false,
  });
};

export const useCustomerCreate = (onSuccess?: any, onError?: any) => {
  const { user } = useAppSelector((state) => state.auth);
  const requestUrl = REACT_APP_API_URL + '/customers/create';
  return useMutation(
    (formPayload: any) => {
      return axios.post(`${requestUrl}`, { ...formPayload, company_id: user?.company_id, sales_staff: user?.id });
    },
    { onError, onSuccess },
  );
};

export const useCustomerUpdate = (onSuccess?: any, onError?: any) => {
  const { user } = useAppSelector((state) => state.auth);
  const requestUrl = REACT_APP_API_URL + '/customers/update';
  return useMutation(
    (formPayload: any) => {
      return axios.post(`${requestUrl}`, { ...formPayload, company_id: user?.company_id, sales_staff: user?.id });
    },
    { onError, onSuccess },
  );
};

export const useCustomerDelete = (onSuccess?: any, onError?: any) => {
  const { user } = useAppSelector((state) => state.auth);
  const requestUrl = REACT_APP_API_URL + '/customers/delete';
  return useMutation(
    (formPayload: any) => {
      return axios.post(`${requestUrl}`, { ...formPayload, company_id: user?.company_id });
    },
    { onError, onSuccess },
  );
};

// Firma güvenilirlik sorgusu (Gemini API)
export const useCompanyTrustCheck = (onSuccess?: any, onError?: any) => {
  const requestUrl = REACT_APP_API_URL + '/gemini/generate';
  return useMutation(
    (companyName: string) => {
      const prompt = `${companyName} ile cari olarak çalışıyoruz. bu carinin finansal olarak risk doğurup doğurmadığından güncel bilgilerine bakarak hareket etmek istiyoruz. bu firma şu anda güvenilir mi? güvenilir değilse neden güvenilir olmadığını da belirt. Yanıtını
bana sadece şöyle dönüş yap.

{
guven:"Güvenilmez",
guvenilmeme_sebebi:"NEDENİ"
}

Veya

{
guven:"Güvenilir",
guvenilmeme_sebebi:""
}`;

      const systemInstruction = `Sen bir finansal risk analisti olarak görev yapıyorsun. Verilen firma hakkında güncel bilgilere dayanarak güvenilirlik değerlendirmesi yap. Yanıtını sadece belirtilen JSON formatında ver, başka hiçbir şey ekleme.`;

      return axios.post(`${requestUrl}`, { prompt, systemInstruction });
    },
    { onError, onSuccess },
  );
};

// Doğal dil ile filtreleme (Gelişmiş Filtreler → AI ile Filtrele)
// Kullanıcı cümlesini + mevcut seçenek listelerini backend'e gönderir,
// doğrulanmış AdvancedFilters parçası döner: { filters, explanation, unmatched }
export const useAiCustomerFilter = (onSuccess?: any, onError?: any) => {
  const requestUrl = REACT_APP_API_URL + '/customers/ai-filter';
  return useMutation(
    (payload: { text: string; availableGroups?: string[]; availableSectors?: string[]; availableCities?: string[]; availableProgrammes?: string[] }) =>
      axios.post(`${requestUrl}`, payload, { timeout: 60000 }),
    { onError, onSuccess },
  );
};

// ============ POTANSIYEL MÜŞTERİLER ============

// Satışçıya gelen yeniden dağıtım partileri (Gelişmiş Filtre > Yeniden Dağıtım Partisi seçenekleri)
export const useMyRedistributionBatches = (enabled = true) =>
  useQuery(
    ['my-redistribution-batches'],
    async () => {
      const res = await axios.get<any[]>(`${REACT_APP_API_URL}/potential-customer-lifecycle/my-redistribution-batches`);
      return res?.data || [];
    },
    { enabled, staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false },
  );

export const usePotentialCustomersList = (params: any, onError?: any) => {
  const { user } = useAppSelector((state) => state.auth);
  const requestParams = { ...params, sales_staff: user?.id };
  const requestUrl = REACT_APP_API_URL + '/potential-customers/list';
  const fetchData = async () => {
    const res = await axios.get<any>(`${requestUrl}`, { params: requestParams });
    return res?.data || {};
  };
  return useQuery(['potential-customers-list', params], fetchData, {
    refetchOnWindowFocus: false,
    onError,
    staleTime: Infinity,
    retry: false,
  });
};

export const usePotentialCustomerCreate = (onSuccess?: any, onError?: any) => {
  const { user } = useAppSelector((state) => state.auth);
  const requestUrl = REACT_APP_API_URL + '/potential-customers/create';
  return useMutation(
    (formPayload: any) => {
      return axios.post(`${requestUrl}`, { ...formPayload, sales_staff: user?.id });
    },
    { onError, onSuccess },
  );
};

// Kayıt AÇMADAN AI benzerlik ön kontrolü ("Benzerlik Kontrolünü Yap" butonu):
// temizse {clean:true} döner, benzerlik varsa taslak onay talebi + approval_required döner
export const usePotentialCustomerPrecheck = (onSuccess?: any, onError?: any) => {
  const { user } = useAppSelector((state) => state.auth);
  const requestUrl = REACT_APP_API_URL + '/potential-customers/precheck';
  return useMutation((formPayload: any) => axios.post(`${requestUrl}`, { ...formPayload, sales_staff: user?.id }, { timeout: 180000 }), { onError, onSuccess });
};

export const useCheckNameSimilarity = () => {
  const requestUrl = REACT_APP_API_URL + '/potential-customers/check-name-similarity';
  return useMutation((name: string) => axios.post(requestUrl, { name }));
};

// AI benzerlik onay akışı: create yanıtı approval_required dönerse
// taslak talebi açıklama ile onaya gönder / vazgeç
export const useSubmitApprovalRequest = (onSuccess?: any, onError?: any) =>
  useMutation(
    (payload: { id: number; note?: string }) => axios.post(`${REACT_APP_API_URL}/potential-customers/approval-requests/${payload.id}/submit`, { note: payload.note || null }),
    { onError, onSuccess },
  );

export const useCancelApprovalRequest = (onSuccess?: any, onError?: any) =>
  useMutation((id: number) => axios.post(`${REACT_APP_API_URL}/potential-customers/approval-requests/${id}/cancel`), { onError, onSuccess });

// Reddedilen potansiyel müşteri talepleri: kullanıcının kendisi + (ekibi varsa)
// ekipteki herkes — Müşterilerim sayfasındaki "Reddedilen" sekmesini besler
export const useRejectedApprovalRequests = (onError?: any) => {
  const requestUrl = REACT_APP_API_URL + '/potential-customers/approval-requests/rejected';
  const fetchData = async () => {
    const res = await axios.get<any>(requestUrl);
    return res?.data || {};
  };
  return useQuery(['rejected-approval-requests'], fetchData, {
    refetchOnWindowFocus: false,
    onError,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
};

// Reddedilen talebe itiraz: zorunlu açıklama ile talep yeniden onaya (pending) düşer
export const useAppealApprovalRequest = (onSuccess?: any, onError?: any) =>
  useMutation((payload: { id: number; note: string }) => axios.post(`${REACT_APP_API_URL}/potential-customers/approval-requests/${payload.id}/appeal`, { note: payload.note }), {
    onError,
    onSuccess,
  });

// Ad değiştiyse sunucu benzerlik taraması yapıp (AI, uzun sürebilir) ad değişikliği
// onay taslağı döner (approval_required + request_type='rename'); diğer alanlar hemen kaydedilir.
export const usePotentialCustomerUpdate = (onSuccess?: any, onError?: any) => {
  const { user } = useAppSelector((state) => state.auth);
  const requestUrl = REACT_APP_API_URL + '/potential-customers/update';
  return useMutation(
    (formPayload: any) => {
      return axios.post(`${requestUrl}`, { ...formPayload, sales_staff: user?.id }, { timeout: 180000 });
    },
    { onError, onSuccess },
  );
};

export const usePotentialCustomerDelete = (onSuccess?: any, onError?: any) => {
  const requestUrl = REACT_APP_API_URL + '/potential-customers/delete';
  return useMutation(
    (formPayload: any) => {
      return axios.post(`${requestUrl}`, { ...formPayload });
    },
    { onError, onSuccess },
  );
};

export const useConvertToCustomer = (onSuccess?: any, onError?: any) => {
  const { user } = useAppSelector((state) => state.auth);
  const requestUrl = REACT_APP_API_URL + '/potential-customers/convert-to-customer';
  return useMutation(
    (formPayload: any) => {
      return axios.post(`${requestUrl}`, { ...formPayload, sales_staff: user?.id });
    },
    { onError, onSuccess },
  );
};

export const useAllCustomersForSelect = (onError?: any) => {
  const { user } = useAppSelector((state) => state.auth);
  const requestParams = { sales_staff: user?.id };
  const requestUrl = REACT_APP_API_URL + '/potential-customers/all-for-select';
  const fetchData = async () => {
    const res = await axios.get<any>(`${requestUrl}`, { params: requestParams });
    return res?.data || {};
  };
  return useQuery(['all-customers-for-select'], fetchData, {
    refetchOnWindowFocus: false,
    onError,
    staleTime: Infinity,
    retry: false,
  });
};

// ERP + Potansiyel müşteriler için async select hook
export const useAllCustomersForSelectAsync = (search: string, enabled: boolean = true, onError?: any) => {
  const { user } = useAppSelector((state) => state.auth);

  const requestParams = {
    sales_staff: user?.id,
    session_id: user?.dia_session_id,
    sales_staff_key: user?.sales_staff_key,
    search,
    user_id: user?.id,
  };
  const requestUrl = REACT_APP_API_URL + '/potential-customers/all-for-select-async';
  const fetchData = async () => {
    const res = await axios.get<any>(`${requestUrl}`, { params: requestParams });
    return res?.data || {};
  };
  return useQuery(['all-customers-for-select-async', search], fetchData, {
    refetchOnWindowFocus: false,
    onError,
    staleTime: 30000, // 30 saniye cache
    retry: false,
    enabled: enabled && !!user?.id,
  });
};

// ERP müşterileri fetch fonksiyonu (manuel çağrı için)
export const fetchAllCustomersForSelectAsync = async (params: {
  sales_staff?: number;
  session_id?: string;
  sales_staff_key?: string;
  search?: string;
  user_id?: number;
  no_sales_filter?: boolean;
}) => {
  const requestUrl = REACT_APP_API_URL + '/potential-customers/all-for-select-async';
  const res = await axios.get<any>(`${requestUrl}`, { params });
  return res?.data || {};
};

// ============ GERÇEK MÜŞTERİLER (DIA sold_serial_numbers) ============

export const useRealCustomersList = (params: any, onError?: any) => {
  const requestParams = { ...params };
  const requestUrl = REACT_APP_API_URL + '/dia/realCustomers';
  const fetchData = async () => {
    const res = await axios.get<any>(`${requestUrl}`, { params: requestParams });
    return res?.data || {};
  };

  const salesRepValue = params.sales_rep || '';

  return useQuery(['real-customers-list', params], fetchData, {
    refetchOnWindowFocus: false,
    onError,
    staleTime: 60000, // 1 dakika cache
    retry: false,
    enabled: !!salesRepValue && salesRepValue.length > 0,
  });
};

export const useCrtProgrammes = () => {
  const requestUrl = REACT_APP_API_URL + '/crt-customers/programmes';
  const fetchData = async () => {
    const res = await axios.get<any>(requestUrl);
    return res?.data || [];
  };
  return useQuery(['crt-programmes'], fetchData, {
    refetchOnWindowFocus: false,
    staleTime: 300000,
    retry: false,
  });
};

export const useMajorCustomerCount = (salesRep: string, onError?: any) => {
  const requestUrl = REACT_APP_API_URL + '/dia/realCustomers';
  const fetchData = async () => {
    const res = await axios.get<any>(requestUrl, {
      params: { sales_rep: salesRep, customer_type: 'major', sale_status: 'sold', limit: 1, page: 1 },
    });
    return res?.data?.pagination?.totalResults || 0;
  };
  return useQuery(['major-customer-count', salesRep], fetchData, {
    refetchOnWindowFocus: false,
    onError,
    staleTime: 60000,
    retry: false,
    enabled: !!salesRep && salesRep.length > 0,
  });
};

export const useCustomerSalesDetail = (customerCode: string | null) => {
  const requestUrl = REACT_APP_API_URL + '/dia/customer-sales-detail/' + encodeURIComponent(customerCode || '');
  const fetchData = async () => {
    const res = await axios.get<any>(requestUrl);
    return res?.data || {};
  };
  return useQuery(['customer-sales-detail', customerCode], fetchData, {
    refetchOnWindowFocus: false,
    staleTime: 60000,
    retry: false,
    enabled: !!customerCode,
  });
};

// ============ MÜŞTERİ HAREKET GEÇMİŞİ (AKTİVİTE ZAMAN ÇİZELGESİ) ============

export const useCustomerActivityHistory = (customerType: 'potential' | 'erp' | null, idOrCode: number | string | null, customerName?: string) => {
  const requestUrl = REACT_APP_API_URL + '/customers/activity-history';
  const fetchData = async () => {
    const params: any = { customer_type: customerType };
    if (customerType === 'potential') params.id = idOrCode;
    else {
      params.code = idOrCode;
      if (customerName) params.name = customerName;
    }
    const res = await axios.get<any>(requestUrl, { params });
    return res?.data || { events: [], total: 0 };
  };
  return useQuery(['customer-activity-history', customerType, idOrCode], fetchData, {
    refetchOnWindowFocus: false,
    staleTime: 60000,
    retry: false,
    enabled: !!customerType && !!idOrCode,
  });
};

// ============ POTANSIYEL MÜŞTERİ İLGİLİ KİŞİLER ============

export const usePotentialCustomerContactsList = (potential_customer_id: number | null, onError?: any) => {
  const requestUrl = REACT_APP_API_URL + '/potential-customers/contacts/list';
  const fetchData = async () => {
    const res = await axios.get<any>(`${requestUrl}`, { params: { potential_customer_id } });
    return res?.data || [];
  };
  return useQuery(['potential-customer-contacts', potential_customer_id], fetchData, {
    refetchOnWindowFocus: false,
    onError,
    staleTime: Infinity,
    retry: false,
    enabled: !!potential_customer_id,
  });
};

// ============ ERP MÜŞTERİ İLGİLİ KİŞİLER ============

export const useErpCustomerContactsList = (customer_code: string | null, onError?: any) => {
  const requestUrl = REACT_APP_API_URL + '/quotes/erp-contacts/list';
  const fetchData = async () => {
    const res = await axios.get<any>(`${requestUrl}`, { params: { customer_code } });
    return res?.data?.data || [];
  };
  return useQuery(['erp-customer-contacts', customer_code], fetchData, {
    refetchOnWindowFocus: false,
    onError,
    staleTime: Infinity,
    retry: false,
    enabled: !!customer_code,
  });
};

export const usePotentialCustomerContactCreate = (onSuccess?: any, onError?: any) => {
  const requestUrl = REACT_APP_API_URL + '/potential-customers/contacts/create';
  return useMutation(
    (formPayload: any) => {
      return axios.post(`${requestUrl}`, formPayload);
    },
    { onError, onSuccess },
  );
};

export const usePotentialCustomerContactUpdate = (onSuccess?: any, onError?: any) => {
  const requestUrl = REACT_APP_API_URL + '/potential-customers/contacts/update';
  return useMutation(
    (formPayload: any) => {
      return axios.post(`${requestUrl}`, formPayload);
    },
    { onError, onSuccess },
  );
};

export const usePotentialCustomerContactDelete = (onSuccess?: any, onError?: any) => {
  const requestUrl = REACT_APP_API_URL + '/potential-customers/contacts/delete';
  return useMutation(
    (formPayload: any) => {
      return axios.post(`${requestUrl}`, formPayload);
    },
    { onError, onSuccess },
  );
};

export const useErpCustomerContactCreate = (onSuccess?: any, onError?: any) => {
  const requestUrl = REACT_APP_API_URL + '/quotes/erp-contacts/create';
  return useMutation(
    (formPayload: any) => {
      return axios.post(`${requestUrl}`, formPayload);
    },
    { onError, onSuccess },
  );
};

// ============ MÜŞTERİ TEMEL BİLGİ ANKETİ (Potansiyel + ERP) ============

export const usePotentialCustomerSurvey = (potential_customer_id: number | null, onError?: any, erp_customer_id?: string | null) => {
  const requestUrl = REACT_APP_API_URL + '/potential-customers/survey';
  const fetchData = async () => {
    const params: any = {};
    if (erp_customer_id) {
      params.erp_customer_id = erp_customer_id;
    } else {
      params.potential_customer_id = potential_customer_id;
    }
    const res = await axios.get<any>(`${requestUrl}`, { params });
    return res?.data || null;
  };
  const queryKey = erp_customer_id ? ['potential-customer-survey', 'erp', erp_customer_id] : ['potential-customer-survey', potential_customer_id];
  return useQuery(queryKey, fetchData, {
    refetchOnWindowFocus: false,
    onError,
    staleTime: Infinity,
    retry: false,
    enabled: erp_customer_id ? !!erp_customer_id : !!potential_customer_id,
  });
};

export const usePotentialCustomerSurveySave = (onSuccess?: any, onError?: any) => {
  const requestUrl = REACT_APP_API_URL + '/potential-customers/survey';
  return useMutation(
    (formPayload: any) => {
      return axios.post(`${requestUrl}`, formPayload);
    },
    { onError, onSuccess },
  );
};

export const usePotentialCustomerMarketingInfo = (potential_customer_id: number | null, onError?: any) => {
  const requestUrl = REACT_APP_API_URL + '/potential-customers/marketing-info';
  const fetchData = async () => {
    const res = await axios.get<any>(`${requestUrl}`, { params: { potential_customer_id } });
    return res?.data || {};
  };
  return useQuery(['potential-customer-marketing-info', potential_customer_id], fetchData, {
    refetchOnWindowFocus: false,
    enabled: !!potential_customer_id,
    onError,
    staleTime: 1000 * 60 * 5,
    retry: false,
  });
};

// ============ MÜŞTERİ NOTLARI (Birleşik - Potansiyel + ERP) ============

export const useCustomerNotesList = (customerType: 'potential' | 'erp', onError?: any) => {
  const requestUrl = REACT_APP_API_URL + '/customer-notes/all';
  const fetchData = async () => {
    const res = await axios.get<any>(`${requestUrl}`, { params: { customer_type: customerType } });
    return res?.data || {};
  };
  return useQuery(['customer-notes-list', customerType], fetchData, {
    refetchOnWindowFocus: false,
    onError,
    staleTime: 30000,
    retry: false,
  });
};

export const useCustomerNoteSave = (onSuccess?: any, onError?: any) => {
  const requestUrl = REACT_APP_API_URL + '/customer-notes/save';
  return useMutation(
    (formPayload: any) => {
      return axios.post(`${requestUrl}`, formPayload);
    },
    { onError, onSuccess },
  );
};

export const useCustomerNoteDelete = (onSuccess?: any, onError?: any) => {
  const requestUrl = REACT_APP_API_URL + '/customer-notes/delete';
  return useMutation(
    (formPayload: any) => {
      return axios.post(`${requestUrl}`, formPayload);
    },
    { onError, onSuccess },
  );
};

// ============ KAYITLI FİLTRELER ============

export const useSavedCustomerFilters = () => {
  const requestUrl = REACT_APP_API_URL + '/saved-customer-filters/list';
  const fetchData = async () => {
    const res = await axios.get<any>(requestUrl);
    return res?.data || [];
  };
  return useQuery(['saved-customer-filters'], fetchData, {
    refetchOnWindowFocus: false,
    staleTime: 30000,
    retry: false,
  });
};

export const useSavedCustomerFilterCreate = (onSuccess?: any, onError?: any) => {
  const requestUrl = REACT_APP_API_URL + '/saved-customer-filters/create';
  return useMutation((formPayload: any) => axios.post(requestUrl, formPayload), { onError, onSuccess });
};

export const useSavedCustomerFilterUpdate = (onSuccess?: any, onError?: any) => {
  const requestUrl = REACT_APP_API_URL + '/saved-customer-filters/update';
  return useMutation((formPayload: any) => axios.post(requestUrl, formPayload), { onError, onSuccess });
};

export const useSavedCustomerFilterDelete = (onSuccess?: any, onError?: any) => {
  const requestUrl = REACT_APP_API_URL + '/saved-customer-filters/delete';
  return useMutation((formPayload: any) => axios.post(requestUrl, formPayload), { onError, onSuccess });
};
